import React, { useMemo } from 'react'
import { Stage, Layer, Text } from 'react-konva'

/**
 * CustomerKonvaPreview - A read-only Konva canvas that shows the template
 * with customer-entered text overlaid in real-time.
 *
 * This is a simplified version of KonvaCanvas for customer use - no editing,
 * dragging, or selection. Just a visual preview.
 */

// Calculate canvas dimensions from template with responsive scaling
function useCanvasDimensions(scalingFactor, templateWidth, templateHeight, maxWidth = 600) {
  const factor = scalingFactor || 1
  if (templateWidth && templateHeight) {
    const baseWidth = templateWidth / factor
    const baseHeight = templateHeight / factor

    // Scale down if wider than maxWidth
    if (baseWidth > maxWidth) {
      const scale = maxWidth / baseWidth
      return {
        width: maxWidth,
        height: baseHeight * scale,
        scale: scale
      }
    }
    return { width: baseWidth, height: baseHeight, scale: 1 }
  }
  return { width: 400, height: 300, scale: 1 }
}

// Calculate font size that fits text within width (auto-fit)
function calculateAutoFitFontSize(text, maxWidth, maxHeight, baseFontSize, minFontSize = 8, fontFamily = 'Arial', wrap = false, letterSpacing = 0) {
  if (!text || maxWidth <= 0) return baseFontSize

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  let fontSize = baseFontSize
  const safeWidth = maxWidth * 0.85
  const safeHeight = maxHeight * 0.85

  while (fontSize > minFontSize) {
    ctx.font = `${fontSize}px ${fontFamily}`

    if (wrap) {
      const words = text.split(' ')
      let line = ''
      let lineCount = 1
      const lineHeight = fontSize * 1.3

      for (let word of words) {
        const testLine = line + (line ? ' ' : '') + word
        const metrics = ctx.measureText(testLine)
        const totalWidth = metrics.width + (testLine.length * letterSpacing)
        if (totalWidth > safeWidth && line) {
          line = word
          lineCount++
        } else {
          line = testLine
        }
      }

      const totalHeight = lineCount * lineHeight
      if (totalHeight <= safeHeight) {
        return fontSize
      }
    } else {
      const metrics = ctx.measureText(text)
      const totalWidth = metrics.width + (text.length * letterSpacing)
      if (totalWidth <= safeWidth) {
        return fontSize
      }
    }

    fontSize--
  }

  return minFontSize
}

// Single text field renderer
function PreviewTextField({ field, displayText, scale }) {
  const scaledLeft = (field.x || 0) * scale
  const scaledTop = (field.y || 0) * scale
  const scaledWidth = (field.width || 100) * scale
  const scaledHeight = (field.height || 30) * scale

  const baseFontSize = (field.text_size || field.textSize || 16) * scale
  const minFontSize = 8 * scale

  // Calculate effective font size with auto-fit if enabled
  const effectiveFontSize = field.auto_fit || field.autoFit
    ? calculateAutoFitFontSize(
        displayText,
        scaledWidth,
        scaledHeight,
        baseFontSize,
        minFontSize,
        field.fontName || field.font?.name || 'Arial',
        field.wrap,
        (field.spacing || 0) * scale
      )
    : baseFontSize

  return (
    <Text
      x={scaledLeft}
      y={scaledTop}
      width={scaledWidth}
      height={scaledHeight}
      text={displayText}
      fontSize={effectiveFontSize}
      fontFamily={field.fontName || field.font?.name || 'Arial'}
      fontStyle={field.fontStyle || 'normal'}
      textDecoration={field.textDecoration || ''}
      fill={field.color || '#000000'}
      letterSpacing={(field.spacing || 0) * scale}
      lineHeight={1.2}
      align={field.align || 'left'}
      verticalAlign="top"
      wrap={field.wrap ? 'word' : 'none'}
    />
  )
}

export default function CustomerKonvaPreview({
  template,
  fields,
  formData,
  imageUrl,
  maxWidth = 600
}) {
  const dimensions = useCanvasDimensions(
    template.scaling_factor,
    template.image_width,
    template.image_height,
    maxWidth
  )

  // Get the text to display for each field
  const getFieldDisplayText = (field) => {
    const fieldName = field.name || field.column_name || field.columnName

    // If customer has entered data, use it
    if (fieldName && formData[fieldName]) {
      return formData[fieldName]
    }

    // Fall back to field's default/sample text
    return field.sample_text || field.sampleText || field.text || ''
  }

  // Memoize rendered fields to avoid unnecessary re-renders
  const renderedFields = useMemo(() => {
    return fields.map((field) => {
      const displayText = getFieldDisplayText(field)

      // Skip fields with no text to display
      if (!displayText) return null

      return (
        <PreviewTextField
          key={field.id}
          field={field}
          displayText={displayText}
          scale={dimensions.scale}
        />
      )
    }).filter(Boolean)
  }, [fields, formData, dimensions.scale])

  return (
    <div style={{
      position: 'relative',
      width: dimensions.width,
      height: dimensions.height,
      margin: '0 auto',
      border: '1px solid #e0e0e0',
      borderRadius: '4px',
      overflow: 'hidden',
      backgroundColor: '#f5f5f5'
    }}>
      {/* Background template image */}
      <img
        src={imageUrl}
        alt="Template background"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: dimensions.width,
          height: dimensions.height,
          display: 'block'
        }}
      />

      {/* Konva canvas overlay for text */}
      <Stage
        width={dimensions.width}
        height={dimensions.height}
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        <Layer>
          {renderedFields}
        </Layer>
      </Stage>
    </div>
  )
}
